/**
 * CONNECTION MANAGER - Centralized Realtime Management
 * 
 * Single manager that handles ALL realtime connectivity:
 * - Connection state management
 * - Auto-reconnection logic
 * - Visibility and focus event handling
 * - Health monitoring
 * - State persistence
 */

import { supabase } from '../../lib/supabase'
import { logger } from '../../utils/logger'

interface ConnectionListener {
  (isConnected: boolean): void
}

export class ConnectionManager {
  private realtimeChannels: Map<string, any> = new Map()
  private listeners: Set<ConnectionListener> = new Set()
  private isConnected = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 2000
  private healthCheckInterval: NodeJS.Timeout | null = null
  private visibilityCheckTimeout: NodeJS.Timeout | null = null

  constructor() {
    this.setupEventHandlers()
    this.startHealthCheck()
    logger.info('ConnectionManager initialized', { component: 'ConnectionManager' })
  }

  private setupEventHandlers() {
    if (typeof window === 'undefined') return

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        logger.debug('Page became visible - checking connection', { 
          component: 'ConnectionManager' 
        })
        this.scheduleConnectionCheck()
      }
    })

    window.addEventListener('focus', () => {
      logger.debug('Window focused - checking connection', { 
        component: 'ConnectionManager' 
      })
      this.scheduleConnectionCheck()
    })

    window.addEventListener('online', () => {
      logger.info('Network came online - reconnecting', { 
        component: 'ConnectionManager' 
      })
      this.reconnectAll()
    })

    window.addEventListener('offline', () => {
      logger.warn('Network went offline', { component: 'ConnectionManager' })
      this.setConnectionStatus(false)
    })
  }

  private scheduleConnectionCheck() {
    if (this.visibilityCheckTimeout) {
      clearTimeout(this.visibilityCheckTimeout)
    }

    this.visibilityCheckTimeout = setTimeout(() => {
      this.checkConnectionHealth()
    }, 1000)
  }

  private startHealthCheck() {
    this.healthCheckInterval = setInterval(() => {
      this.checkConnectionHealth()
    }, 30000)
  }

  private async checkConnectionHealth() {
    let actuallyConnected = true

    for (const [channelName, channel] of this.realtimeChannels) {
      if (!channel || channel.state !== 'joined') {
        actuallyConnected = false
        logger.warn('Channel not properly connected', {
          component: 'ConnectionManager',
          data: { channelName, state: channel?.state }
        })
        break
      }
    }

    if (this.isConnected && !actuallyConnected) {
      logger.warn('Connection state mismatch detected - reconnecting', {
        component: 'ConnectionManager'
      })
      await this.reconnectAll()
    } else if (!this.isConnected && actuallyConnected) {
      logger.info('Connection restored', { component: 'ConnectionManager' })
      this.setConnectionStatus(true)
    }
  }

  async createChannel(
    channelName: string, 
    config: {
      table: string
      schema?: string
      event?: string
      onUpdate?: (payload: any) => void
      onBroadcast?: (payload: any) => void
    }
  ): Promise<boolean> {
    try {
      if (this.realtimeChannels.has(channelName)) {
        await this.removeChannel(channelName)
      }

      const channel = supabase
        .channel(channelName, {
          config: {
            broadcast: { self: false }
          }
        })
        .on(
          'postgres_changes' as any,
          {
            event: config.event || '*',
            schema: config.schema || 'public',
            table: config.table
          },
          (payload: any) => {
            if (config.onUpdate) {
              config.onUpdate(payload)
            }
          }
        )
        .on(
          'broadcast',
          { event: 'SLOT_UPDATE' },
          (event: any) => {
            if (config.onBroadcast && event.payload) {
              config.onBroadcast(event.payload)
            }
          }
        )
        .subscribe((status: any) => {
          this.handleChannelStatusChange(channelName, status)
        })

      this.realtimeChannels.set(channelName, channel)
      
      logger.info('Channel created with postgres_changes and broadcast', {
        component: 'ConnectionManager',
        data: { channelName, table: config.table }
      })

      return true
    } catch (error) {
      logger.error('Failed to create channel', {
        component: 'ConnectionManager',
        data: { channelName, error }
      })
      return false
    }
  }

  sendBroadcast(channelName: string, event: string, payload: any) {
    const channel = this.realtimeChannels.get(channelName)
    if (channel && channel.state === 'joined') {
      try {
        channel.send({
          type: 'broadcast',
          event,
          payload
        })
      } catch (err) {
        logger.error('Failed to send realtime broadcast', {
          component: 'ConnectionManager',
          data: { channelName, event, err }
        })
      }
    }
  }

  async removeChannel(channelName: string): Promise<void> {
    const channel = this.realtimeChannels.get(channelName)
    if (channel) {
      try {
        await channel.unsubscribe()
        this.realtimeChannels.delete(channelName)
        logger.info('Channel removed', {
          component: 'ConnectionManager',
          data: { channelName }
        })
      } catch (error) {
        logger.error('Failed to remove channel', {
          component: 'ConnectionManager',
          data: { channelName, error }
        })
      }
    }
  }

  private handleChannelStatusChange(channelName: string, status: string) {
    logger.debug('Channel status changed', {
      component: 'ConnectionManager',
      data: { channelName, status }
    })

    if (status === 'SUBSCRIBED') {
      this.reconnectAttempts = 0
      this.setConnectionStatus(true)
    } else if (status === 'CLOSED' && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect()
    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      this.setConnectionStatus(false)
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect()
      }
    }
  }

  private scheduleReconnect() {
    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)
    
    logger.info('Scheduling reconnection attempt', {
      component: 'ConnectionManager',
      data: { attempt: this.reconnectAttempts, delay }
    })

    setTimeout(() => {
      this.reconnectAll()
    }, delay)
  }

  private async reconnectAll() {
    if (this.realtimeChannels.size === 0) return

    logger.info('Reconnecting all channels', {
      component: 'ConnectionManager',
      data: { channelCount: this.realtimeChannels.size }
    })

    const channelConfigs: Array<{ name: string; channel: any }> = []
    for (const [name, channel] of this.realtimeChannels) {
      channelConfigs.push({ name, channel })
    }

    for (const config of channelConfigs) {
      try {
        await config.channel.unsubscribe()
      } catch (error) {
        logger.error('Failed to unsubscribe channel during reconnect', {
          component: 'ConnectionManager',
          data: { channelName: config.name, error }
        })
      }
    }

    this.realtimeChannels.clear()
    this.setConnectionStatus(false)
  }

  private setConnectionStatus(connected: boolean) {
    if (this.isConnected !== connected) {
      this.isConnected = connected
      
      logger.info('Connection status changed', {
        component: 'ConnectionManager',
        data: { connected }
      })

      this.listeners.forEach(listener => {
        try {
          listener(connected)
        } catch (error) {
          logger.error('Connection listener error', {
            component: 'ConnectionManager',
            data: error
          })
        }
      })
    }
  }

  addListener(listener: ConnectionListener): () => void {
    this.listeners.add(listener)
    listener(this.isConnected)
    return () => {
      this.listeners.delete(listener)
    }
  }

  isRealtimeConnected(): boolean {
    return this.isConnected
  }

  getChannelCount(): number {
    return this.realtimeChannels.size
  }

  async destroy() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
    }

    if (this.visibilityCheckTimeout) {
      clearTimeout(this.visibilityCheckTimeout)
    }

    for (const [name, channel] of this.realtimeChannels) {
      try {
        await channel.unsubscribe()
      } catch (error) {
        logger.error('Failed to cleanup channel', {
          component: 'ConnectionManager',
          data: { name, error }
        })
      }
    }

    this.realtimeChannels.clear()
    this.listeners.clear()
    this.setConnectionStatus(false)

    logger.info('ConnectionManager destroyed', { component: 'ConnectionManager' })
  }
}

export const connectionManager = new ConnectionManager()
